import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function RoofClawV2Launch() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="We Launched RoofClaw V2 Today. Here's What We Learned."
            date="April 18, 2026"
            readTime="6 MIN"
            heroImage="/images/blog/roofclaw-v2-launch-hero.jpg"
            description="New pricing, new capabilities, two live onboarding sessions, and one moment that stopped the room cold. Here's what happened the day RoofClaw V2 went live."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Today was one of those days that reminds you why you do this.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We launched RoofClaw V2 — new pricing, new pages, new capabilities live on the site. And while the code was shipping, we were on back-to-back onboarding sessions with two clients from the same Colorado roofing company. One machine for customer service. One for marketing. Two very different jobs.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's what happened, and what it means for where RoofClaw is going.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What's new in V2</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                RoofClaw started at $5,000. Good product. Real value. But we knew it wasn't the full picture.
            </p>

            <p className="mb-8 text-[#F0EFF4]/80 text-lg leading-relaxed">
                V2 is $10,000 USD. And the stack is deeper:
            </p>

            <div className="space-y-4 mb-12">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">EVERYTHING FROM V1</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        Dedicated Apple hardware, full build, up to 3 API integrations, 3-hour delivery session, Winning Wednesday weekly support. That foundation didn't change.
                    </p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">TONY "THE WRENCH" CLAWZINI</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        A second AI agent that lives on the machine as a break/fix mechanic. If your main agent ever hits a wall, Tony diagnoses and fixes it. He's separate, always available, and he doesn't need a help ticket.
                    </p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">ROOFCLAW COMMUNITY</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        A private Telegram group where all clients share wins, ask questions, and learn from each other. Vito monitors it daily. Adam handles the escalations. $99/month after a 6-month free trial — the first recurring revenue in RoofClaw history.
                    </p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">ZOOM PRE-INSTALLED</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        Sounds small. It's not. Every onboarding session is smoother when the client doesn't have to install anything before we can start. Ships on every machine going forward.
                    </p>
                </div>
            </div>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                And for contractors who aren't sure they're ready: we now offer an <strong className="text-white">$800 AI Readiness Session</strong> — a 60-minute strategy call with Adam that maps your business to an AI build plan. That $800 credits toward the full build if you move forward. It's the lowest-risk way to start.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What the sessions taught us today</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We did two onboarding sessions today. Back to back.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The first client runs customer service for the company. His agent handles inbound calls, qualifies inspection requests, and manages the booking flow. His bar is high — he doesn't want generic AI outputs. He wants results that look like his team made them. The agent is built to that standard.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The second client runs marketing for the same company. Her agent handles SEO, ads, social, and content production. She's already a content creator — Canva, Reels, HubSpot scheduling, Runway. The agent doesn't replace her workflow. It removes the production bottleneck.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Then we showed her two short AI-generated videos. A Goldilocks-style roofer story. A personalized thank-you video.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                She said: "Oh my god." Then: "I'm in, Adam."
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's the product working exactly the way it's supposed to.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The insight nobody talks about</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's the problem every roofing company has: they can't get their sales guys to record videos.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Everyone knows video works. Nobody shoots it. The owner is busy. The crew is on a roof. The phone camera stays in somebody's pocket.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A marketing agent changes that equation. CompanyCam captures every job. The agent pulls those photos via API, feeds them to an AI video tool, and turns finished jobs into social content — automatically. The roofer does the work. The agent turns it into marketing.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                No one has to record anything.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We haven't fully productized this yet. But that reaction in the room told us everything we need to know about where it's going.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The two-machine model</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                One of our clients bought two RoofClaws for two different jobs. That's the model.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You wouldn't hire one person to run both customer service <em>and</em> your marketing department. Neither should your AI.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">MACHINE 1</p>
                    <p className="text-white font-semibold mb-2">Customer-Facing Agent</p>
                    <p className="text-[#F0EFF4]/80 text-sm leading-relaxed">
                        Inbound, qualification, booking, client experience. Handles the conversations so your team can close.
                    </p>
                </div>
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">MACHINE 2</p>
                    <p className="text-white font-semibold mb-2">Marketing Engine</p>
                    <p className="text-[#F0EFF4]/80 text-sm leading-relaxed">
                        Content, ads, SEO, video, social. Turns your finished jobs into marketing without anyone recording a video.
                    </p>
                </div>
            </div>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Two machines. Two specialized agents. One roofing company that runs tighter than the competition.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                One of our roofing clients is already living this model. They're proof it works.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What's next</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                V2 is live. The site reflects the new pricing, the new value stack, and the new way we're thinking about what a roofing AI system should actually do.
            </p>

            <p className="mb-10 text-[#F0EFF4]/80 text-lg leading-relaxed">
                If you're curious whether you're ready, the $800 AI Readiness Session is the place to start. No obligation. Real strategy. That $800 credits toward the full build if you move forward.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8 mb-4">
                <a
                    href="https://www.roofingbusinesspartner.com/meetings/adamsandlinks/roofclaw-ai-readiness"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors duration-200"
                >
                    Book Your AI Readiness Session — $800
                </a>
                <a
                    href="https://buy.stripe.com/4gM6oG1SQfKz5PmegX2kw09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block border border-[#7B61FF] hover:bg-[#7B61FF]/10 text-[#7B61FF] font-semibold px-8 py-4 rounded-xl text-center transition-colors duration-200"
                >
                    Get RoofClaw V2 — $10,000 USD
                </a>
            </div>
        </BlogLayout>
    );
}
